import { ThemeIcon, Tooltip } from '@mantine/core';
import { IconRobot, IconUserCircle } from '@tabler/icons-react';
import { TEAMMATE_INK, TEAMMATE_TINT, TeammateAvatar, teammateRing } from '@/components/TeamAvatar';

/**
 * The top-right "who did this" avatar every timeline card header carries.
 *
 * Canon: activity-timeline header anatomy — teammate-written cards take initials
 * on a Dark Green tint, system-written cards take the robot glyph on a Dark
 * Brown tint. The distinction is the point: on a financial card, "a webhook
 * recorded this" and "a colleague keyed this" call for different follow-up.
 *
 * A named teammate renders through TeammateAvatar, so the header avatar wears
 * the same role ring as every other teammate avatar in Orbit (BAR-2571).
 * `name` is optional because an actor is not always recoverable — a treatment
 * payment order records no creator today (BAR-2526 adds the Provenance) — and
 * the teammate variant then degrades to a generic person glyph rather than
 * inventing an attribution.
 */
const SYSTEM_TINT = 'rgba(72, 22, 0, 0.12)';
const SYSTEM_INK = '#481600';

export function ActorAvatar({
  variant,
  label,
  name,
}: {
  variant: 'teammate' | 'system';
  /** Tooltip and accessible name — say who, or say why we cannot. */
  label: string;
  /** The teammate's display name, when known — initials and role ring come from it. */
  name?: string;
}) {
  const system = variant === 'system';

  if (!system && name) {
    return <TeammateAvatar name={name} size={24} label={label} />;
  }

  return (
    <Tooltip label={label} position="top" withArrow>
      <ThemeIcon
        variant="filled"
        radius="xl"
        size={24}
        styles={{
          root: {
            backgroundColor: system ? SYSTEM_TINT : TEAMMATE_TINT,
            color: system ? SYSTEM_INK : TEAMMATE_INK,
            boxShadow: system ? undefined : teammateRing(undefined),
          },
        }}
        aria-label={label}
        role="img"
      >
        {system ? <IconRobot size={14} /> : <IconUserCircle size={14} />}
      </ThemeIcon>
    </Tooltip>
  );
}
